import { type CSSProperties, type ReactNode } from 'react'
import { Card, CardContent } from '../../../components/ui'
import './gradientSphereCard.css'

type GradientSphereCardTag = 'article' | 'div' | 'section'

interface GradientSphereCardStyle extends CSSProperties {
  '--gradient-sphere-color'?: string
}

interface GradientSphereCardProps {
  sphereColor: string
  children: ReactNode
  className?: string
  as?: GradientSphereCardTag
  ariaLabel?: string
}

interface GradientSphereMetricCardProps {
  label: string
  value: ReactNode
  sphereColor: string
  className?: string
  valueSize?: 'default' | 'lg' | 'xl'
  labelVariant?: 'default' | 'section'
}

export const GRADIENT_SPHERE_PALETTE = [
  'rgba(157, 163, 175, 0.38)',
  'rgba(157, 163, 175, 0.32)',
  'rgba(157, 163, 175, 0.34)',
  'rgba(157, 163, 175, 0.32)',
] as const

function classNames(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ')
}

export function sphereColorAt(index: number): string {
  return GRADIENT_SPHERE_PALETTE[index % GRADIENT_SPHERE_PALETTE.length]
}

export default function GradientSphereCard({
  sphereColor,
  children,
  className,
  as = 'div',
  ariaLabel,
}: GradientSphereCardProps) {
  const Component = as
  const style: GradientSphereCardStyle = { '--gradient-sphere-color': sphereColor }

  return (
    <Card
      as={Component}
      className={classNames('gradient-sphere-card shadow-none', className)}
      style={style}
      aria-label={ariaLabel}
    >
      <CardContent className="gradient-sphere-card__content p-0">{children}</CardContent>
    </Card>
  )
}

export function GradientSphereMetricCard({
  label,
  value,
  sphereColor,
  className,
  valueSize = 'default',
  labelVariant = 'default',
}: GradientSphereMetricCardProps) {
  const valueClassName =
    valueSize === 'xl'
      ? 'text-3xl font-semibold'
      : valueSize === 'lg'
        ? 'text-2xl font-semibold'
        : ''
  const labelClassName =
    labelVariant === 'section'
      ? 'dash-type-section-label text-base/6 font-medium uppercase tracking-[0.04em] text-zinc-500 dark:text-zinc-400'
      : ''

  return (
    <GradientSphereCard sphereColor={sphereColor} className={classNames('gradient-sphere-metric-card', className)}>
      <p className={classNames('gradient-sphere-metric-card__value', valueClassName)}>{value}</p>
      <span className={classNames('gradient-sphere-metric-card__label', labelClassName)}>{label}</span>
    </GradientSphereCard>
  )
}
